// Help articles, Portuguese (Brazil): AI-drafted from articles.en.js on 2026-11-01, marked
// for review (HELP_SYSTEM_PLAN.md, D3). Same ids, categories, screens and links as the
// English; on-screen names match the app's own Portuguese labels. Example wording a
// candidate would say in the interview stays in English.

export const REVIEW_STATUS = "ai_draft";

export const CATEGORIES = [
  { id: "start", title: "Primeiros passos" },
  { id: "setup", title: "Preparar uma entrevista" },
  { id: "sound", title: "Microfone e som" },
  { id: "during", title: "Durante a entrevista" },
  { id: "live", title: "Modo Avançado" },
  { id: "report", title: "Seu relatório e suas pontuações" },
  { id: "account", title: "Conta, sessões e privacidade" },
  { id: "free", title: "Teste Gratuito" },
];

export const ARTICLES = [
  // ── Primeiros passos ─────────────────────────────────────────────────────
  {
    id: "how-it-works", category: "start", screens: ["upload", "account"],
    title: "Como o simulador funciona",
    body: [
      { p: "O English Job Interview Simulator permite que você pratique uma entrevista de emprego real em inglês e mostra exatamente como melhorar." },
      { steps: [
        "Envie [seu currículo](#upload-resume) e a [descrição da vaga](#job-description) para a função que você deseja.",
        "Escolha seu modo de prática: [Modo Guiado ou Modo Avançado](#guided-or-live).",
        "Responda em voz alta às 10 perguntas da entrevista. O entrevistador pode fazer uma breve [pergunta de acompanhamento](#follow-ups).",
        "Leia [seu relatório](#report-overview): seu desempenho na entrevista, o feedback sobre seu inglês profissional e uma [versão aprimorada de cada resposta](#polished-answer).",
      ] },
      { p: "As perguntas são criadas a partir do seu currículo e da descrição da vaga, então cada entrevista é diferente." },
    ],
  },
  {
    id: "app-language", category: "start", screens: ["account"],
    title: "Escolher o idioma do aplicativo",
    body: [
      { p: "O idioma do aplicativo é o idioma das telas, das instruções e do feedback. Você o escolhe ao se cadastrar e pode alterá-lo em Configurações da conta, no menu no canto superior direito." },
      { p: "A entrevista em si é sempre em inglês: as perguntas, suas respostas e as [respostas aprimoradas](#polished-answer). As explicações e as dicas ficam no idioma do aplicativo." },
      { p: "O aplicativo está disponível em inglês, português (Brasil), espanhol (América Latina) e francês (Canadá)." },
    ],
  },
  {
    id: "partner-code", category: "start", screens: ["account"],
    title: "Usar um Código de Parceiro",
    body: [
      { p: "Um Código de Parceiro dá a você acesso, ou condições especiais de acesso, por meio de um de nossos parceiros. Você pode digitá-lo ao se cadastrar e criar o perfil da sua conta, ou depois em Configurações da conta." },
      { steps: [
        "Abra Configurações da conta.",
        "Digite o código no campo Código de Parceiro, exatamente como você o recebeu.",
        "Selecione Aplicar código. A tela mostra a nova [data de término do seu acesso](#access-sessions).",
      ] },
      { note: "Se o código não for aceito, verifique se há espaços ou letras digitadas errado. Se ainda assim não funcionar, entre em contato com o parceiro que forneceu o código." },
    ],
  },
  {
    id: "sign-in", category: "start", screens: ["login"],
    title: "Entrar e redefinir sua senha",
    body: [
      { p: "Você pode entrar com seu e-mail e senha, ou com sua conta Google se tiver se cadastrado com o Google." },
      { steps: [
        "Para redefinir sua senha, selecione a aba Recuperar senha na parte de cima da caixa de entrada.",
        "Digite o e-mail que você usou no cadastro.",
        "Abra o e-mail que enviamos e siga o link para escolher uma nova senha.",
      ] },
      { note: "Não recebeu o e-mail? Verifique a pasta de spam ou lixo eletrônico e confirme que digitou o mesmo endereço usado no cadastro." },
    ],
  },

  // ── Preparar uma entrevista ──────────────────────────────────────────────
  {
    id: "upload-resume", category: "setup", screens: ["upload"],
    title: "Enviar seu currículo",
    body: [
      { p: "Envie o mesmo currículo que você enviaria a um empregador, em PDF ou Word (.docx)." },
      { list: [
        "Seu currículo precisa estar em inglês. Um currículo bilíngue funciona se pelo menos metade dele estiver em inglês.",
        "Nomes de empresas, escolas e cargos em outros idiomas não são problema.",
        "Antes de qualquer outra coisa, a [verificação de privacidade](#privacy-scan) remove seus dados pessoais. Depois, você revisa o resultado.",
      ] },
      { note: "Se o seu currículo for recusado por não estar em inglês, envie uma versão em inglês." },
    ],
  },
  {
    id: "privacy-scan", category: "setup", screens: ["upload"],
    title: "O que a verificação de privacidade remove",
    body: [
      { p: "Antes de o seu currículo ser usado, a verificação de privacidade procura dados pessoais e os substitui por rótulos neutros, como [Name] ou [Company]." },
      { list: [
        "Dados de contato: e-mails, números de telefone, links de sites e redes sociais.",
        "Seu nome e o nome de outras pessoas.",
        "Endereços e números de identificação.",
      ] },
      { p: "Suas habilidades, sua experiência e seus cargos permanecem, porque as perguntas da entrevista precisam deles." },
      { note: "Sempre leia o texto verificado antes de continuar. A verificação remove o máximo possível, mas não é perfeita: revise o texto e faça as alterações necessárias. Depois, marque a caixa para confirmar que revisou o texto e que aprova seu uso na entrevista. Veja também [Seus dados e sua privacidade](#data-privacy)." },
    ],
  },
  {
    id: "job-description", category: "setup", screens: ["upload"],
    title: "Descrição da vaga real ou de exemplo",
    body: [
      { p: "Para uma prática mais realista, cole ou envie a descrição real da vaga para a qual você está se candidatando." },
      { p: "Se você não está se candidatando a uma vaga específica e só quer praticar suas habilidades de entrevista, escolha uma descrição de vaga de exemplo. Você pode escolher uma área e um nível, ou deixar que o aplicativo crie uma descrição de vaga de exemplo adequada ao seu currículo." },
      { p: "As perguntas da entrevista combinam seu currículo com a descrição da vaga para criar um conjunto de perguntas personalizado para a sua entrevista. Se o currículo enviado e a descrição da vaga tiverem muito pouco em comum, você verá o [aviso de compatibilidade](#fit-check)." },
    ],
  },
  {
    id: "fit-check", category: "setup", screens: ["upload"],
    title: "O que significa o aviso de compatibilidade",
    body: [
      { p: "Se o seu currículo e a descrição da vaga tiverem muito pouco em comum, você verá um aviso antes de a entrevista ser criada. Boas perguntas de prática precisam de algo de ambos." },
      { note: "O aviso não é uma avaliação sobre se você é a pessoa certa para a vaga. Ele só significa que o simulador pode ter dificuldade para criar perguntas relevantes para a sua entrevista." },
      { p: "Você pode:" },
      { list: [
        "Enviar outra descrição de vaga.",
        "Deixar o aplicativo sugerir uma função de prática adequada ao seu currículo.",
        "Praticar mesmo assim.",
      ] },
      { p: "Ver o aviso e trocar a descrição da vaga não afeta a contagem de sessões. Praticar mesmo assim usa uma [sessão](#what-uses-session) normal de entrevista." },
    ],
  },
  {
    id: "guided-or-live", category: "setup", screens: ["upload"],
    title: "Modo Guiado ou Modo Avançado: qual escolher",
    body: [
      { p: "Os dois modos usam as mesmas perguntas e a mesma avaliação. A diferença está em como a entrevista acontece." },
      { list: [
        "Modo Guiado: você pode ver cada pergunta, [ouvi-la de novo](#replay-question), gravar quando estiver pronto, ouvir sua resposta e regravá-la antes de enviar. Veja [Gravar e concluir uma resposta](#guided-recording).",
        "Modo Avançado: como uma entrevista real. Só áudio, sem texto. Você tem 30 segundos para começar a responder, e o entrevistador continua quando você para de falar. Veja [Como o Modo Avançado funciona](#live-how).",
      ] },
      { p: "Comece pelo Modo Guiado se você é iniciante em entrevistas em inglês. Escolha o Modo Avançado quando quiser praticar em condições reais." },
      { note: "O [Teste Gratuito](#free-trial) usa o Modo Guiado. O Modo Avançado faz parte do [acesso completo de 30 dias](#access-sessions)." },
    ],
  },
  {
    id: "choose-voice", category: "setup", screens: ["upload"],
    title: "Escolher a voz do entrevistador",
    body: [
      { p: "Escolha um entrevistador com voz feminina ou masculina em Configurações da sessão. Selecione Ouvir esta voz para ouvir uma breve amostra antes." },
      { p: "A voz lê todas as perguntas, perguntas de acompanhamento e comentários da entrevista. Você pode ajustar o volume a qualquer momento no controle de volume ao lado de Ajuda. Veja [Sem som ou volume muito baixo](#no-sound)." },
    ],
  },

  // ── Microfone e som ──────────────────────────────────────────────────────
  {
    id: "check-sound", category: "sound", screens: ["upload", "warmup", "interview", "live"],
    title: "Verificar meu microfone e o som",
    body: [
      { steps: [
        "Se possível, use fones de ouvido.",
        "Na tela de preparação, onde você escolhe a voz feminina ou masculina do entrevistador, selecione Ouvir esta voz. Se não ouvir nada, veja [Sem som ou volume muito baixo](#no-sound).",
        "Use a [pergunta de aquecimento](#warm-up) antes da entrevista para gravar algumas palavras e ouvi-las.",
        "Se a gravação estiver em silêncio, verifique se o microfone correto está selecionado e [permitido no navegador](#allow-microphone).",
      ] },
      { note: "Um ambiente silencioso faz muita diferença, principalmente no [Modo Avançado](#live-how), em que o silêncio encerra sua resposta." },
    ],
  },
  {
    id: "allow-microphone", category: "sound", screens: ["warmup", "interview", "live"],
    title: "Permitir o microfone no navegador",
    body: [
      { p: "Na primeira vez que você gravar, o navegador pedirá permissão para usar o microfone. Selecione Permitir." },
      { p: "Se você bloqueou por engano:" },
      { steps: [
        "Selecione o ícone de cadeado ou de configurações ao lado do endereço do site.",
        "Encontre Microfone e altere para Permitir.",
        "Recarregue a página.",
      ] },
      { note: "Em um celular ou tablet, verifique também, nas configurações do aparelho, se o navegador tem permissão para usar o microfone. Veja também [Navegadores e dispositivos compatíveis](#browsers)." },
    ],
  },
  {
    id: "no-sound", category: "sound", screens: ["upload", "interview", "live"],
    title: "Sem som ou volume muito baixo",
    body: [
      { list: [
        "Verifique o controle de volume ao lado de Ajuda. Se aparecer um alto-falante riscado, o som está desativado: selecione-o para ativar.",
        "Verifique o volume do seu dispositivo e se os fones de ouvido estão conectados.",
        "Se uma pergunta não tocar no Modo Guiado, selecione Ouvir pergunta. Se o áudio não estiver disponível, verifique as configurações de som do computador ou do dispositivo. Veja [Ouvir uma pergunta de novo](#replay-question).",
      ] },
    ],
  },
  {
    id: "browsers", category: "sound", screens: ["upload", "warmup", "live"],
    title: "Navegadores e dispositivos compatíveis",
    body: [
      { p: "Use uma versão atualizada do Chrome, Edge, Safari ou Firefox. Um computador oferece a melhor experiência, mas tablets e celulares também funcionam." },
      { list: [
        "Seu navegador precisa ter [permissão para usar o microfone](#allow-microphone).",
        "O Modo Avançado precisa de um navegador que grave áudio. Se o seu não gravar, o Modo Guiado será oferecido.",
        "Feche outros aplicativos que possam estar usando o microfone, como chamadas de vídeo.",
      ] },
    ],
  },

  // ── Durante a entrevista ─────────────────────────────────────────────────
  {
    id: "warm-up", category: "during", screens: ["warmup"],
    title: "A pergunta de aquecimento",
    body: [
      { p: "Antes de a entrevista começar, você responde a uma pergunta de aquecimento fácil. Ela ajuda você a [testar o microfone](#check-sound) e a se sentir à vontade para falar." },
      { p: "Grave sua resposta e ouça para verificar o som. Nada do aquecimento é enviado para análise nem entra no seu relatório." },
      { p: "Quando estiver pronto, selecione Continuar para a entrevista." },
    ],
  },
  {
    id: "guided-recording", category: "during", screens: ["interview"],
    title: "Gravar e concluir uma resposta (Modo Guiado)",
    body: [
      { steps: [
        "Ouça a pergunta. Você pode [ouvi-la de novo](#replay-question).",
        "Selecione o microfone para começar a gravar e dê sua resposta.",
        "Selecione-o de novo para parar. Você pode ouvir sua gravação.",
        "Selecione Regravar para tentar de novo, ou Enviar resposta para enviá-la.",
      ] },
      { p: "Depois da última pergunta, selecione Ver meu relatório." },
      { note: "Responda como faria em uma entrevista real: um exemplo claro, o que você fez e qual foi o resultado." },
    ],
  },
  {
    id: "replay-question", category: "during", screens: ["interview"],
    title: "Ouvir uma pergunta de novo",
    body: [
      { p: "No Modo Guiado, selecione Ouvir pergunta para ouvi-la de novo, ou Ouvir mais devagar para ouvi-la mais lentamente." },
      { p: "Se o texto da pergunta estiver oculto, selecione Mostrar a pergunta para lê-la." },
      { p: "No Modo Avançado não há botão para repetir: [peça ao entrevistador que repita](#live-repeat), por exemplo \"Could you repeat the question, please?\"" },
    ],
  },
  {
    id: "follow-ups", category: "during", screens: ["interview", "live", "report"],
    title: "Perguntas de acompanhamento",
    body: [
      { p: "Como um entrevistador real, o simulador pode fazer uma breve pergunta de acompanhamento quando falta algo importante na sua resposta, por exemplo um exemplo específico ou o resultado." },
      { list: [
        "Sua resposta à pergunta de acompanhamento é avaliada junto com a resposta principal.",
        "Uma pergunta de acompanhamento não é uma penalidade, mas responder de forma completa na primeira vez é um ótimo hábito em entrevistas. Seu relatório conta essas respostas como [Completas na primeira resposta](#complete-first).",
      ] },
    ],
  },
  {
    id: "restart-or-end", category: "during", screens: ["warmup", "interview"],
    title: "Reiniciar ou Encerrar: o que cada um faz",
    body: [
      { list: [
        "Reiniciar começa a mesma entrevista de novo a partir da Pergunta 1 e apaga suas respostas até então. Seu currículo, a descrição da vaga e as configurações são mantidos. O primeiro reinício de uma entrevista é gratuito; cada reinício seguinte usa uma [sessão](#what-uses-session).",
        "Encerrar entrevista interrompe a entrevista e apaga suas respostas e seu progresso. A sessão usada não é restabelecida.",
      ] },
      { note: "Se você só precisa de uma pausa, não é preciso encerrar a entrevista. Ela continua ativa por 60 minutos, desde que você deixe o aplicativo aberto no navegador." },
    ],
  },
  {
    id: "prep-failed", category: "during", screens: ["prepare"],
    title: "Se a preparação falhar",
    body: [
      { p: "Criar suas perguntas costuma levar menos de um minuto. Se aparecer \"Não foi possível preparar sua entrevista\", selecione Tentar novamente." },
      { p: "Se isso continuar acontecendo, a causa pode estar em nossos servidores. Aguarde alguns minutos e tente de novo, ou fale com o suporte para que possamos verificar." },
    ],
  },

  // ── Modo Avançado ────────────────────────────────────────────────────────
  {
    id: "live-how", category: "live", screens: ["live", "upload"],
    title: "Como o Modo Avançado funciona",
    body: [
      { p: "O Modo Avançado é mais parecido com uma entrevista real: só áudio, sem o texto das perguntas e sem botões para apertar entre as perguntas." },
      { steps: [
        "O entrevistador faz a pergunta.",
        "Seu microfone é ativado e uma [contagem regressiva](#live-countdown) mostra quanto tempo você tem para começar a responder.",
        "Dê sua resposta. Depois que você começa, não há limite de tempo.",
        "Quando você para de falar, o entrevistador continua. Veja [Encerrar uma resposta](#live-ending).",
      ] },
      { note: "Use fones de ouvido em um ambiente silencioso. A ajuda fica disponível entre as perguntas, não enquanto uma pergunta toca nem enquanto você responde. Não sabe qual modo escolher? Veja [Modo Guiado ou Modo Avançado](#guided-or-live)." },
    ],
  },
  {
    id: "live-countdown", category: "live", screens: ["live"],
    title: "A contagem regressiva de 30 segundos",
    body: [
      { p: "Depois de cada pergunta, você tem 30 segundos para começar a responder (20 segundos para uma [pergunta de acompanhamento](#follow-ups)). A contagem para assim que você começa a falar." },
      { p: "Se você não começar a tempo, o entrevistador diz \"Let's move on\" e passa para a próxima pergunta. Seu relatório mostra \"Nenhuma resposta\" para essa pergunta, e ela conta como não respondida." },
      { note: "Tire um momento para pensar e depois comece a falar. Depois de começar, continue: cerca de 3 segundos de silêncio [encerram sua resposta](#live-ending)." },
    ],
  },
  {
    id: "live-ending", category: "live", screens: ["live"],
    title: "Encerrar uma resposta e \"Terminei\"",
    body: [
      { p: "Sua resposta termina quando você fica cerca de 3 segundos sem falar, ou quando seleciona Terminei." },
      { list: [
        "Pausas curtas para pensar não têm problema: os primeiros segundos de uma resposta nunca a encerram.",
        "Selecione Terminei se o barulho ao redor mantiver o microfone ouvindo depois que você terminou.",
        "Tudo o que você disse é avaliado.",
      ] },
    ],
  },
  {
    id: "live-repeat", category: "live", screens: ["live"],
    title: "Pedir ao entrevistador que repita",
    body: [
      { p: "É só pedir, como você faria em uma entrevista real (em inglês):" },
      { list: [
        "\"Could you repeat the question, please?\"",
        "\"Sorry, I didn't understand the question.\"",
      ] },
      { p: "O entrevistador repete a pergunta com as mesmas palavras, e a [contagem regressiva](#live-countdown) recomeça. Pedir uma repetição não conta como resposta." },
    ],
  },

  // ── Seu relatório e suas pontuações ──────────────────────────────────────
  {
    id: "report-overview", category: "report", screens: ["report", "trial-report"],
    title: "Entender seu relatório",
    body: [
      { p: "Seu relatório tem duas pontuações principais, de 0 a 100:" },
      { list: [
        "[Desempenho na Entrevista](#interview-performance): o quanto suas respostas funcionam como respostas de entrevista.",
        "[Comunicação em Inglês Profissional](#english-dimensions): o quanto você se comunica bem em inglês.",
      ] },
      { p: "Abaixo delas, cada pergunta mostra suas pontuações, o que você disse, qualquer [pergunta de acompanhamento](#follow-ups), uma [versão aprimorada da sua resposta](#polished-answer), o que você fez bem e o que melhorar." },
      { p: "As barras ficam verdes a partir de 75, amarelas de 50 a 74 e vermelhas abaixo de 50." },
      { note: "As pontuações e os níveis de inglês são estimativas de prática para orientar sua preparação. Não são uma certificação." },
    ],
  },
  {
    id: "interview-performance", category: "report", screens: ["report", "trial-report"],
    title: "Desempenho na Entrevista: as seis dimensões",
    body: [
      { list: [
        "Relevância à pergunta: você respondeu ao que foi perguntado?",
        "Evidências e exemplos: você deu um exemplo real e específico?",
        "Organização e estrutura: a resposta foi fácil de acompanhar?",
        "Competência profissional: ela mostrou as habilidades que a vaga exige?",
        "Evidência de resultados: você disse qual foi o resultado? Aparece como \"—\" quando a pergunta não pedia um resultado.",
        "Presença profissional: você transmitiu confiança e assumiu responsabilidade?",
      ] },
      { p: "Sua pontuação de Desempenho na Entrevista é a média dessas dimensões. Veja também [Entender seu relatório](#report-overview)." },
    ],
  },
  {
    id: "english-dimensions", category: "report", screens: ["report", "trial-report"],
    title: "Inglês profissional: as cinco dimensões e os níveis do CEFR",
    body: [
      { p: "Seu inglês é avaliado em cinco dimensões: gramática, vocabulário, clareza, tom profissional e fluência." },
      { p: "Cada uma é avaliada com diretrizes parecidas com as do CEFR, a escala internacional de níveis de idioma, e convertida em uma pontuação: cerca de 45 é parecido com B1, 65 é parecido com B2 e 85 é parecido com C1." },
      { list: [
        "Forte: 80 ou mais.",
        "Funcional: de 60 a 79.",
        "Em desenvolvimento: abaixo de 60.",
      ] },
      { p: "Se uma resposta for curta demais para avaliar seu inglês de forma justa, aparece \"Resposta muito curta para avaliar o inglês\", e ela fica de fora da sua pontuação de inglês. Veja também [Entender seu relatório](#report-overview)." },
    ],
  },
  {
    id: "complete-first", category: "report", screens: ["report"],
    title: "\"Completas na primeira resposta\"",
    body: [
      { p: "Este número conta as perguntas que você respondeu de forma completa na primeira vez, sem precisar de uma [pergunta de acompanhamento](#follow-ups)." },
      { p: "Em uma entrevista real, respostas que já trazem um exemplo específico e o resultado causam uma ótima impressão. Use este número para ver com que frequência sua primeira resposta estava completa." },
    ],
  },
  {
    id: "polished-answer", category: "report", screens: ["report", "trial-report"],
    title: "Usar a resposta aprimorada e suas sugestões",
    body: [
      { p: "A resposta aprimorada é a sua própria resposta reescrita em inglês natural e profissional. Ela mantém o que você quis dizer e não acrescenta nada que você não disse." },
      { p: "As sugestões em negrito e em cor, entre colchetes, mostram o que uma resposta mais forte acrescentaria, por exemplo um resultado ou um exemplo específico." },
      { note: "Substitua cada sugestão por detalhes reais da sua experiência e depois pratique dizer a resposta inteira em voz alta. Você pode tentar de novo com [Repraticar](#re-practice)." },
    ],
  },
  {
    id: "re-practice", category: "report", screens: ["report"],
    title: "Repraticar",
    body: [
      { p: "Repraticar permite responder às mesmas perguntas de novo, uma vez, para aplicar o que você aprendeu com seu relatório." },
      { list: [
        "Usa uma [sessão](#what-uses-session).",
        "É oferecido na página do seu relatório logo depois que você termina a entrevista, e deixa de estar disponível quando você sai dessa página ou da sua conta.",
        "Seu relatório compara suas novas pontuações com a primeira tentativa.",
      ] },
      { p: "Para praticar com perguntas novas, prepare uma nova entrevista com o mesmo currículo e a mesma descrição da vaga." },
    ],
  },
  {
    id: "download-report", category: "report", screens: ["report", "downloads"],
    title: "Baixar seu relatório",
    body: [
      { p: "Selecione Baixar Relatório na página do seu relatório para salvá-lo em PDF. Você também pode baixá-lo depois em Meus Relatórios e Downloads, no menu da sua conta." },
      { p: "O PDF inclui suas pontuações, uma página de feedback sobre seu inglês e todas as perguntas, com sua resposta, a resposta aprimorada e o que melhorar." },
      { note: "Os relatórios podem ser baixados até o fim do seu acesso de 30 dias. Veja [Por quanto tempo seus relatórios são mantidos](#report-retention)." },
    ],
  },

  // ── Conta, sessões e privacidade ─────────────────────────────────────────
  {
    id: "access-sessions", category: "account", screens: ["account", "upload"],
    title: "Seu acesso de 30 dias e 10 sessões",
    body: [
      { p: "O acesso completo dura 30 dias e inclui no total até 10 sessões de entrevista, que você pode usar em qualquer dia durante o período de acesso de 30 dias." },
      { list: [
        "Sessões usadas: aparece na barra superior, por exemplo \"Sessões usadas: 3 de 10\". Passe o cursor sobre ela para ver quando seu acesso termina.",
        "As sessões não usadas terminam junto com seu acesso de 30 dias.",
        "Renovar começa um novo período de 30 dias com 10 novas sessões. O acesso não é renovado automaticamente: para renovar, você adquire mais 30 dias de acesso.",
      ] },
      { p: "Veja [O que usa uma sessão](#what-uses-session)." },
    ],
  },
  {
    id: "what-uses-session", category: "account", screens: ["account", "interview"],
    title: "O que usa uma sessão",
    body: [
      { list: [
        "Começar uma nova entrevista, no Modo Guiado ou no Modo Avançado: 1 sessão.",
        "[Repraticar](#re-practice): 1 sessão.",
        "Reiniciar uma entrevista: o primeiro reinício é gratuito; cada reinício seguinte usa 1 sessão. Veja [Reiniciar ou Encerrar](#restart-or-end).",
        "Se uma entrevista não conseguir começar, a sessão é restabelecida.",
        "Se a [preparação da sua entrevista personalizada](#prep-failed) falhar, fale com o suporte. Vamos restabelecer sua contagem de sessões e investigar a causa.",
      ] },
      { p: "Ler seu relatório e baixá-lo nunca usam uma sessão." },
    ],
  },
  {
    id: "report-retention", category: "account", screens: ["account", "downloads", "report"],
    title: "Por quanto tempo seus relatórios são mantidos",
    body: [
      { p: "Seu relatório, e o currículo, a descrição da vaga e as respostas a partir dos quais ele foi criado, são mantidos até o fim do seu período de acesso de 30 dias. Depois disso, tudo é excluído automaticamente." },
      { p: "Enviamos um e-mail cerca de 48 horas antes de seus relatórios serem removidos, para que você possa [baixar o PDF](#download-report). Depois que seu acesso de 30 dias termina, você não pode mais baixar seus relatórios anteriores." },
    ],
  },
  {
    id: "data-privacy", category: "account", screens: ["account", "upload"],
    title: "Seus dados e sua privacidade",
    body: [
      { list: [
        "O currículo que você envia passa por uma [verificação de privacidade](#privacy-scan) antes de ser usado, e você revisa o resultado. O aplicativo nunca usa seu currículo original para criar a entrevista: só a versão que você aprova depois da verificação é usada, e ela é mantida até o fim do seu acesso de 30 dias.",
        "O texto verificado do seu currículo, a descrição da vaga e suas respostas são usados somente para criar e avaliar sua entrevista de prática.",
        "Eles são excluídos no fim do seu período de acesso de 30 dias (depois de 24 horas, no caso de um [Teste Gratuito](#free-deleted)).",
        "Serviços de IA processam sua entrevista para criar as perguntas, transcrever suas respostas e dar feedback.",
      ] },
      { p: "Para todos os detalhes, consulte a Política de Privacidade." },
    ],
  },

  // ── Teste Gratuito ───────────────────────────────────────────────────────
  {
    id: "free-trial", category: "free", screens: ["upload", "trial-report"],
    title: "O que o Teste Gratuito inclui",
    body: [
      { list: [
        "Uma entrevista de prática gratuita de 6 perguntas, no Modo Guiado.",
        "Seus resultados completos na tela, com feedback para cada pergunta.",
        "A mesma [verificação de privacidade](#privacy-scan) do currículo que no acesso completo.",
      ] },
      { p: "Baixar o relatório, [Repraticar](#re-practice), reiniciar e o [Modo Avançado](#live-how) fazem parte do [acesso completo de 30 dias](#access-sessions). Você pode vê-los no aplicativo, em cinza." },
    ],
  },
  {
    id: "free-deleted", category: "free", screens: ["trial-report"],
    title: "Por que seus resultados gratuitos são excluídos",
    body: [
      { p: "Os resultados do Teste Gratuito não são salvos. Eles são excluídos quando você sai da página de resultados ou da sua conta, e automaticamente 24 horas depois da entrevista." },
      { p: "Leia seu feedback antes de sair da página. Com o acesso completo, os relatórios são mantidos até o fim do seu acesso de 30 dias e podem ser [baixados em PDF](#download-report)." },
    ],
  },
];
